// Client-side search for the browse pages (Tools, Registry). Everything is already loaded, so
// filtering is a string match — no request per keystroke.
//
// A query is words, each of which must match. A plain word matches anywhere in an item's
// text. A `field:value` word (`status:deprecated`, `ring:assess`, `kind:vendor`) matches only
// that field, exactly — what a pill click on a detail page puts in the search box, since a
// plain "active" would also match "interactive" in a description. Values with spaces are
// quoted: `kind:"internal platform"`.

/** One item as the search sees it: all its text, lower-cased, and the fields a `field:value`
 * word can name, each with its values. */
export interface Searchable {
  text: string;
  fields: Record<string, (string | null | undefined)[]>;
}

const TOKEN = /([a-z]+):"([^"]*)"|([a-z]+):(\S+)|(\S+)/gi;

/** Whether `item` matches every word of `query`. An empty query matches everything. A word
 * naming a field the item doesn't have (`https://…`, say) is searched as plain text. */
export function matches(item: Searchable, query: string): boolean {
  for (const m of query.matchAll(TOKEN)) {
    const [, qKey, qValue, key, value, word] = m;
    const field = (qKey ?? key)?.toLowerCase();
    const values = field ? item.fields[field] : undefined;
    if (values) {
      const wanted = (qValue ?? value).toLowerCase();
      if (!values.some((v) => (v ?? "").toLowerCase() === wanted)) return false;
    } else if (!item.text.includes((word ?? m[0]).toLowerCase())) {
      return false;
    }
  }
  return true;
}

/** The search word that finds every item whose `field` is `value` — what a pill sets. */
export function fieldQuery(field: string, value: string): string {
  return /\s/.test(value) ? `${field}:"${value}"` : `${field}:${value}`;
}

/** The values a `field:value` word can take, per field — what the search box suggests. */
export type FieldHints = Record<string, { value: string; label?: string }[]>;

/** Every field's values across `items`, de-duplicated and sorted. `labels` gives readable
 * text for values that aren't self-explanatory (a flag's `standards_gap`, say). */
export function fieldHints(items: Searchable[], labels: Record<string, Record<string, string>> = {}): FieldHints {
  const seen: Record<string, Set<string>> = {};
  for (const item of items) {
    for (const [field, values] of Object.entries(item.fields)) {
      for (const v of values) if (v) (seen[field] ??= new Set()).add(v);
    }
  }
  return Object.fromEntries(
    Object.entries(seen).map(([field, values]) => [
      field,
      [...values].sort((a, b) => a.localeCompare(b)).map((value) => ({ value, label: labels[field]?.[value] })),
    ]),
  );
}

/** The words a search box can be told about, for its placeholder: `kind:, ring:, flag:`. */
export function placeholderFor(noun: string, hints: FieldHints): string {
  const fields = Object.keys(hints).filter((f) => hints[f].length > 0);
  if (fields.length === 0) return `Search ${noun}`;
  const shown = fields.slice(0, 3).map((f) => `${f}:`);
  return `Search ${noun}, or try ${shown.join(", ")}${fields.length > shown.length ? ", …" : ""}`;
}

/** One suggestion for the word being typed: the text that replaces it, and how to show it. */
export interface Suggestion {
  insert: string;
  label: string;
  detail?: string;
}

const MAX_SUGGESTIONS = 12;

/** Suggestions for the last word of `query`. Part of a field name (`ki`) suggests the field
 * (`kind:`, with a few example values); a field and colon (`kind:`, `kind:ven`) suggests that
 * field's values that start with what's typed. Nothing for a plain word. */
export function suggest(query: string, hints: FieldHints): Suggestion[] {
  const word = query.match(/\S*$/)?.[0] ?? "";
  if (!word) return [];
  const colon = word.indexOf(":");
  if (colon === -1) {
    return fieldSuggestions(hints).filter((s) => s.insert.startsWith(word.toLowerCase()));
  }
  const field = word.slice(0, colon).toLowerCase();
  const typed = word
    .slice(colon + 1)
    .replace(/^"/, "")
    .toLowerCase();
  return (hints[field] ?? [])
    .filter((v) => v.value.toLowerCase().startsWith(typed))
    .slice(0, MAX_SUGGESTIONS)
    .map((v) => ({ insert: `${fieldQuery(field, v.value)} `, label: `${field}:${v.value}`, detail: v.label }));
}

/** Every field the search understands, each with a few example values — what an empty box
 * lists when asked, so the `field:` words can be discovered without knowing them. */
export function fieldSuggestions(hints: FieldHints): Suggestion[] {
  return Object.keys(hints)
    .filter((f) => hints[f].length > 0)
    .map((f) => ({
      insert: `${f}:`,
      label: `${f}:`,
      detail:
        hints[f]
          .slice(0, 3)
          .map((v) => v.value)
          .join(", ") + (hints[f].length > 3 ? ", …" : ""),
    }));
}

/** `query` with its last word replaced by a picked suggestion. */
export function applySuggestion(query: string, s: Suggestion): string {
  return query.replace(/\S*$/, s.insert);
}

/** When the last word names a known field but a value none of its values start with
 * (`kind:blah`), what to tell the person instead: the values there are. Null otherwise. */
export function invalidValueHint(query: string, hints: FieldHints): string | null {
  const word = query.match(/\S*$/)?.[0] ?? "";
  const colon = word.indexOf(":");
  if (colon === -1 || suggest(query, hints).length > 0) return null;
  const field = word.slice(0, colon).toLowerCase();
  const values = hints[field];
  if (!values) return null;
  return `No ${field} “${word.slice(colon + 1).replace(/"/g, "")}”. Try: ${values.map((v) => v.value).join(", ")}`;
}

/** `query` with the word for `field:value` added, or — if it's already there (ignoring case
 * and quotes) — taken out, leaving the rest of the search as it was. What a ⌘-click on a pill
 * does, where a plain click replaces the search. */
export function toggleField(query: string, field: string, value: string): string {
  const wanted = `${field}:${value}`.toLowerCase();
  const words = [...query.matchAll(TOKEN)].map((m) => m[0]);
  const kept = words.filter((w) => w.replace(/"/g, "").toLowerCase() !== wanted);
  return kept.length < words.length ? kept.join(" ") : [...words, fieldQuery(field, value)].join(" ");
}
